import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPage } from '../services/contentService';
import { StaticPage, PageSection } from '../types';
import { ShieldCheck, Headphones, Globe, Mail, Phone, Users } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';

const IconMap: Record<string, any> = {
  ShieldCheck, Headphones, Globe, Mail, Phone, Users
};

export const CompanyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<StaticPage | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      setLoading(true);
      getPage(slug).then(data => {
        setPage(data);
        setLoading(false);
      });
    }
  }, [slug]);

  if (loading) return <div className="py-20 text-center text-gray-400">Loading...</div>;
  if (!page) return <div className="py-20 text-center text-gray-400">Page not found</div>;

  return (
    <div className="animate-fade-in pb-20">
      <SEOHead 
        title={page.title} 
        description={page.subtitle} 
        image={page.coverImage}
      />
      {/* Page Header */}
      <div className="max-w-4xl mx-auto text-center mb-16">
        <h1 className="text-4xl md:text-6xl font-serif font-black text-brand-900 mb-4 tracking-tight">
          {page.title}
        </h1>
        <p className="text-xl text-gray-500 font-serif italic">
          {page.subtitle}
        </p>
      </div>

      {/* Dynamic Sections */}
      <div className="max-w-4xl mx-auto space-y-16">
        {page.sections.map((section) => (
          <SectionRenderer key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
};

const SectionRenderer: React.FC<{ section: PageSection }> = ({ section }) => {
  switch (section.type) {
    case 'rich_text':
      return (
        <div className="prose prose-lg prose-serif text-gray-800 mx-auto">
          {section.title && <h2 className="text-2xl font-bold mb-4 font-sans tracking-tight">{section.title}</h2>}
          <div dangerouslySetInnerHTML={{ __html: section.content || '' }} />
        </div>
      );

    case 'value_grid':
      return (
        <div className="bg-brand-100 rounded-2xl p-8 md:p-12">
          {section.title && <h2 className="text-center text-2xl font-bold mb-10 uppercase tracking-widest text-brand-900">{section.title}</h2>}
          <div className="grid md:grid-cols-3 gap-8">
            {section.values?.map((val, idx) => {
              const Icon = IconMap[val.icon] || Globe;
              return (
                <div key={idx} className="text-center">
                   <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 text-brand-500 shadow-sm">
                      <Icon size={32} />
                   </div>
                   <h3 className="font-bold text-lg mb-2">{val.title}</h3>
                   <p className="text-sm text-gray-600 leading-relaxed">{val.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      );

    case 'team_grid':
      return (
        <div>
          {section.title && <h2 className="text-2xl font-bold mb-8 border-b border-gray-200 pb-2">{section.title}</h2>}
          <div className="grid md:grid-cols-2 gap-8">
            {section.teamMembers?.map((member) => (
              <div key={member.id} className="flex gap-4 items-start p-4 rounded-xl hover:bg-gray-50 transition-colors">
                <img src={member.avatar} alt={member.name} className="w-20 h-20 rounded-full object-cover border border-gray-200" />
                <div>
                   <h3 className="font-bold text-lg text-brand-900">{member.name}</h3>
                   <div className="text-xs font-bold text-brand-500 uppercase tracking-wider mb-2">{member.role}</div>
                   <p className="text-sm text-gray-600">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'contact_list':
      return (
        <div className="grid md:grid-cols-2 gap-6">
           {section.contactItems?.map((item, idx) => (
             <div key={idx} className="border border-gray-200 rounded-lg p-6 flex items-center justify-between group hover:border-brand-500 transition-colors bg-white shadow-sm">
                <div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{item.label}</div>
                  <div className="text-lg font-bold text-brand-900">{item.value}</div>
                </div>
                <div className="text-gray-300 group-hover:text-brand-500 transition-colors">
                   {item.type === 'email' ? <Mail /> : <Phone />}
                </div>
             </div>
           ))}
        </div>
      );

    default:
      return null;
  }
};